// RevenueCoversChartStyled.jsx
import React from "react";
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  Chart as ChartJS,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Color } from "chart.js/helpers";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import Loader from "../../Components/Common/Loader";
import { useRef } from "react";

// Register Chart.js components and plugins
ChartJS.register(
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  zoomPlugin
);

const RevenueCoversChartStyled = ({ mealPeriod, breakDownData, loading }) => {
  const labels = mealPeriod || ["Breakfast", "Lunch", "Afternoon", "Dinner"];

  // Colors for bars and lines
  const revenueColors = ["#45B7D1", "#96CEB4", "#4ECDC4"];
  const coverColors = ["#FF9F40", "#FF6B6B", "#9966FF"];

  const revenueDatasets = breakDownData.map((item, idx) => {
    const baseColor = revenueColors[idx % revenueColors.length];

    return {
      type: "bar",
      label: `${item.name} Revenue`,
      data: item.revenue_data.map(Number),
      backgroundColor: baseColor,
      yAxisID: "yRevenue",
      order: 2, // Bars rendered first
      borderRadius: 4, // Rounded bar edges
      maxBarThickness: 40,
    };
  });

  const coversDatasets = breakDownData.map((item, idx) => ({
    type: "line",
    label: `${item.name} Covers`,
    data: item.covers_data.map(Number),
    borderColor: coverColors[idx % coverColors.length],
    backgroundColor: "transparent",
    tension: 0.4,
    pointBackgroundColor: coverColors[idx % coverColors.length],
    pointRadius: 0,
    order: 1, // Lines above bars
    fill: false,
    yAxisID: "yCovers",
    datalabels: {
      display: true,
      backgroundColor: coverColors[idx % coverColors.length],
      color: "#fff",
      borderRadius: 5,
      padding: 4,
      font: { weight: "bold", size: 10 },
      align: "center",
      anchor: "center",
    },
  }));

  const data = {
    labels,
    datasets: [...revenueDatasets, ...coversDatasets],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false, // 👈 Allow parent div to control height
    interaction: {
      mode: "index",
      axis: "x",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          pointStyleWidth: 8,
          boxWidth: 8,
          boxHeight: 6,
        },
      },
      tooltip: {
        enabled: false, // disable default tooltip
        external: (context) => {
          const tooltipModel = context.tooltip;

          // Tooltip element
          let tooltipEl = document.getElementById("chartjs-tooltip");
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.transition = "opacity 0.2s ease";
            tooltipEl.style.opacity = 0;
            tooltipEl.style.position = "absolute";
            tooltipEl.style.zIndex = 999;
            document.body.appendChild(tooltipEl);
          }

          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          // Set tooltip content
          const titleLines = tooltipModel.title || [];
          const bodyLines = tooltipModel.body.map((b, i) => {
            const dataPoint = tooltipModel.dataPoints[i];
            const dataset = context.chart.data.datasets[dataPoint.datasetIndex];

            const [label, value] = b.lines[0].split(":");
            // Use borderColor for line datasets and backgroundColor for bar datasets
            const circleColor =
              dataset.type === "line"
                ? dataset.borderColor
                : dataset.backgroundColor;

            return `
              <div style="
                display: flex;
                align-items: center;
                font-size: 13px;
                font-weight: 400;
                text-align: left;
                line-height: 1.8;
                margin-bottom: 4px;
                color: #8b8a99;
                border:1px solid #
              ">
                <span style="
                  display: inline-block;
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background: ${circleColor};
                  margin-right: 8px;
                "></span>
                <span>${label}:</span>&nbsp;
                <span style="font-weight: 700;">${parseInt(value.replace(/,/g, '').split('.')[0], 10)}</span>
      
              </div>
            `;
          });

          tooltipEl.innerHTML = `
            <div style="
              background: #ffffff;
              border: 1px solid #e0e0e0;
              border-radius: 10px;
              box-shadow: 0px 2px 8px rgba(0,0,0,0.1);
              color: #333333;
              overflow: hidden;
           
            ">
              <div style="
                background-color: #f6f8fb; /* Title background color */
                color: #8b8a99;
                padding: 8px 12px;
                font-size: 13px;
                text-align: left;
                border-bottom:1px solid #e0e0e0
              ">
                ${titleLines.join("<br/>")}
              </div>
              <div style="padding: 8px 12px;">
                ${bodyLines.join("")}
              </div>
            </div>
          `;

          // Position tooltip
          const chartRect = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left =
            chartRect.left + window.pageXOffset + tooltipModel.caretX + "px";
          tooltipEl.style.top =
            chartRect.top + window.pageYOffset + tooltipModel.caretY + "px";
        },
      },

      datalabels: {
        display: (ctx) => ctx.dataset.type === "line",
        align: "top",
        color: (ctx) => ctx.dataset.borderColor,
        font: {
          weight: "bold",
        },
        formatter: (value) => value,
      },

      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          drag: {
            enabled: true, // 👉 this enables drag-to-zoom
            backgroundColor: 'rgba(225,225,225,0.3)',
            borderColor: 'rgba(0,0,0,0.3)',
            borderWidth: 1,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
        limits: {
          x: { min: 'original', max: 'original' },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 0,
      },
      line: {
        tension: 0.4,
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      yRevenue: {
        type: "linear",
        position: "left",
        ticks: { callback: (v) => `${v}` },
        grid: { display: false },
        title: {
          display: true,
          text: "Revenue",
          color: "#555555",
          font: { size: 14, weight: "bold" },
        },
      },
      yCovers: {
        type: "linear",
        position: "right",
        grid: { display: false },
        title: {
          display: true,
          text: "Covers",
          color: "#555555",
          font: { size: 14, weight: "bold" },
          // font: { weight: "bold" },
        }
      },
    },
  };

  // Inside component
  const chartRef = useRef(null);

  // Reset handler
  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  return (

    <Row>
      <Card style={{ minHeight: "470px", width: "100%" }}>
        <CardHeader className="ps-3 border-0 align-items-center d-flex">
          <div className="d-flex align-items-center">
            <div className={`icon-box rounded bg-danger text-white`}>
              <i className="ri-bar-chart-line"></i>
            </div>
            <div className="ps-2">
              <h4 className="card-title mb-0 flex-grow-1">
                Revenue Breakdown by Meal Period
              </h4>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {loading && <Loader />}
          <div className={loading ? "opacity-0" : ""} style={{ height: "350px", width: "100%" }}>
            {/* <div className="d-flex justify-content-end pe-4"><button onClick={handleResetZoom} className="btn justify-content-end" style={{fontSize:"large"}}><i className="ri-home-4-fill"></i></button></div> */}
            <Chart ref={chartRef} data={data} options={options} />
          </div>
        </CardBody>
      </Card>

    </Row>)
};

export default RevenueCoversChartStyled;
